import React from 'react';
import { AlertCircle, X } from 'lucide-react';
import type { ValidationError } from '../../services/queries/validationRuleQueries';

interface ValidationErrorsProps {
  errors: ValidationError[];
  onDismiss?: () => void;
  className?: string;
}

/**
 * ValidationErrors Component
 *
 * Displays validation rule errors in a styled alert box.
 * Use this in edit/create modals to show validation failures.
 *
 * @example
 * {validationErrors.length > 0 && (
 *   <ValidationErrors
 *     errors={validationErrors}
 *     onDismiss={() => setValidationErrors([])}
 *   />
 * )}
 */
export const ValidationErrors: React.FC<ValidationErrorsProps> = ({
  errors,
  onDismiss,
  className = ''
}) => {
  if (errors.length === 0) return null;

  return (
    <div
      className={`p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg ${className}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-red-900 dark:text-red-200 mb-2">
            Validation Error{errors.length > 1 ? 's' : ''}
          </h4>
          <ul className="space-y-1.5">
            {errors.map((error, index) => (
              <li
                key={error.ruleId || index}
                className="text-sm text-red-800 dark:text-red-300 flex items-start gap-2"
              >
                <span className="text-red-400 dark:text-red-500 mt-0.5">•</span>
                <span>
                  {error.errorMessage}
                  {error.fieldLabel && error.errorLocation === 'field' && (
                    <span className="ml-1 text-xs text-red-600 dark:text-red-400 font-medium">
                      ({error.fieldLabel})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Dismiss validation errors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ValidationErrors;
